import { cn } from "@/lib/utils";
import { Tag } from "@/types/Tag";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/ui/command";
import { Trans, useLingui } from "@lingui/react/macro";
import { CheckIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";

type TagSelectorProps = {
  onAddNewTag: (newTag: string) => void;
  onChange: (value: string[]) => void;
  newTags: string[];
  value: string[];
  tags: Tag[];
};

export default function TagSelector({
  onAddNewTag,
  onChange,
  newTags,
  tags,
  value,
}: TagSelectorProps) {
  const { t } = useLingui();

  // Local state
  const [tagSearch, setTagSearch] = useState<string>("");

  // Helpers
  const tagSearchTrimmed = useMemo(() => tagSearch.trim(), [tagSearch]);

  const allTags = useMemo(() => {
    return [...(tags || []), ...newTags];
  }, [tags, newTags]);

  return (
    <Command>
      <CommandInput
        placeholder={t`Sök taggar...`}
        className="h-9"
        autoFocus
        onValueChange={setTagSearch}
        value={tagSearch}
      />
      <CommandList
        style={{
          height: 500,
          width: "100%",
          overflow: "auto",
        }}
      >
        <CommandEmpty>
          {tagSearchTrimmed ? (
            <Button
              type="button"
              variant="outline"
              className="max-w-sm"
              onClick={() => {
                onAddNewTag(tagSearchTrimmed);

                onChange([...(value || []), tagSearchTrimmed]);

                setTagSearch("");
              }}
            >
              <PlusIcon />

              <span className="truncate">
                <Trans>
                  Skapa en &ldquo;{tagSearchTrimmed}
                  &rdquo; tagg
                </Trans>
              </span>
            </Button>
          ) : (
            <span className="text-muted-foreground">
              <Trans>Inga taggar hittades</Trans>
            </span>
          )}
        </CommandEmpty>

        <CommandGroup>
          {allTags.map((tag) => {
            const isNewTag = typeof tag === "string";

            const tagName = isNewTag ? tag : tag.name;
            const tagValue = isNewTag ? tag : tag.slug;

            const isChecked = value?.includes(tagValue);

            return (
              <CommandItem
                key={tagValue}
                onSelect={() => {
                  let updatedTags: string[] | undefined;

                  if (isChecked) {
                    updatedTags = value?.filter((tag) => tag !== tagValue);
                  } else {
                    updatedTags = [...(value || []), tagValue];
                  }

                  onChange(updatedTags);
                }}
              >
                <CheckIcon
                  className={cn(
                    "mr-2 h-4 w-4",
                    isChecked ? "opacity-100" : "opacity-0"
                  )}
                />

                <span className="truncate">{tagName}</span>

                <Badge
                  variant="purple"
                  className={cn(
                    "ml-auto",
                    isNewTag && isChecked ? "opacity-100" : "opacity-0"
                  )}
                >
                  <Trans>Kommer skapas</Trans>
                </Badge>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
